import { isEmpty } from "lodash";
import { NFTConstraint, NFTConstraintParam } from "../event/constraint/nft";
import { FormField } from "../form";
import { MS_END } from "../timezone";
import { normalizeId, toJSON } from "../utils";
import {
  AutoDiscountConfig,
  AutoDiscountConfigParam,
  Discount,
} from "./discount";
import { IPaymentPrice, PaymentPrice } from "./price";

export enum PaymentMethod {
  PAYPAL = "paypal",
  STRIPE = "stripe",
  COINGATE = "coingate",
}

export enum PaymentError {
  UNKNOWN = "payment/unknown",
  UNKNOWN_TRANSACTION = "payment/unknown_transaction",
  UNKNOWN_EVENT = "payment/unknown_event",
  UNKNOWN_USER = "payment/unknown_user",
  UNKNOWN_PRODUCT = "payment/unknown_product",
  UNKNOWN_EMAIL = "payment/unknown_email",
  UNKNOWN_PAYMENT_METHOD = "payment/unknown_payment_method",
  SAVE_TRANSACTION_FAILED = "payment/save_transaction_failed",
  CREATE_PAYMENT_SESSION_FAILED = "payment/create_payment_session_failed",
  TRANSACTION_MODIFIED = "payment/transaction_modified",
  INVITE_ONLY = "payment/invite_only",
  WHITELIST_ONLY = "payment/whitelist_only",

  DISCOUNT_MODIFIED = "payment/discount_modified",
  PURCHASE_LIMIT_EXCEEDED = "payment/purchase_limit_exceeded",
  DISCOUNT_LIMIT_EXCEEDED = "payment/discount_limit_exceeded",
  SOLD_OUT = "payment/sold_out",
  SALES_NOT_STARTED = "payment/sales_not_started",
  SALES_ENDED = "payment/sales_ended",
}

export const DEFAULT_MAX_PER_TRANSACTION = 5;
export interface PaymentConfigParam
  extends Omit<
    Partial<PaymentConfig>,
    "prices" | "nftConstraint" | "autoDiscounts"
  > {
  prices?: IPaymentPrice[];
  nftConstraint?: NFTConstraintParam;
  autoDiscounts?: AutoDiscountConfigParam[];
}

export class PaymentConfig implements PaymentConfigParam {
  prices: PaymentPrice[];
  max: number; // -1 for unlimited.  Equivalent to whitelistConstraint.max
  customForm?: FormField[];
  inviteOnly?: boolean;
  maxPerTransaction: number; // Max quantity per transaction
  maxPerUser?: number; // Max transaction per user account (search by identifiers).
  start: number; // Sales start
  end: number; // Sales end
  nftConstraint?: NFTConstraint;
  maxPerNFT?: number; // Only if nftConstraint is set. Max quantity per NFT held, -1 for unlimited.
  disableBuyForOthers?: boolean; // If set, set maxPerTransaction to 1
  whitelist?: string[]; // string or regex string. If set, set disableBuyForOthers to true
  autoDiscounts?: AutoDiscountConfig[];

  constructor(params?: PaymentConfigParam) {
    this.prices =
      params?.prices && !isEmpty(params.prices)
        ? params.prices
            .map((price) => new PaymentPrice(price))
            .sort((a, b) => a.end - b.end || a.price - b.price)
        : [new PaymentPrice()];
    this.max = Number(params?.max || -1);
    this.maxPerTransaction = Number(
      params?.maxPerTransaction || DEFAULT_MAX_PER_TRANSACTION
    );
    this.start = Number(params?.start ?? 0);
    this.end = Number(params?.end ?? MS_END);

    if (params?.customForm) this.customForm = params.customForm;
    if (params?.inviteOnly) this.inviteOnly = !!params.inviteOnly;
    if (params?.nftConstraint) {
      this.nftConstraint = new NFTConstraint(params.nftConstraint);
      this.maxPerNFT = Number(params?.maxPerNFT || -1);
    }
    if (params?.maxPerUser) this.maxPerUser = Number(params.maxPerUser);
    if (params?.disableBuyForOthers) this.disableBuyForOthers = true;
    if (params?.whitelist && !isEmpty(params.whitelist)) {
      this.whitelist = params.whitelist.map((w) => normalizeId(w));
      if (!params?.maxPerTransaction) {
        this.disableBuyForOthers = true;
        this.maxPerTransaction = 1;
      }
    }
    if (this.disableBuyForOthers) this.maxPerTransaction = 1;

    if (params?.autoDiscounts && !isEmpty(params.autoDiscounts)) {
      this.autoDiscounts = params.autoDiscounts
        .filter(
          (d) =>
            d.criteria &&
            d.discount &&
            !isEmpty(d.criteria) &&
            !isEmpty(d.discount)
        )
        .map((d, i) => ({
          priority: d.priority ?? i,
          criteria: d.criteria,
          discount: new Discount({ ...d.discount, reusable: true, auto: true }),
        }))
        .sort((a, b) => a.priority - b.priority);
    }
  }

  get json() {
    return toJSON(this);
  }

  get defaultPrice() {
    try {
      return this.prices.sort((a, b) => b.end - a.end || b.price - a.price)[0];
    } catch (error) {}
    return this.prices[0];
  }

  get currentPrice() {
    try {
      if (this.prices.length == 1) return this.prices[0];
      const now = new Date().valueOf();
      this.prices = this.prices.sort(
        (a, b) => a.end - b.end || a.price - b.price
      );
      const n = this.prices.length;
      for (let i = 0; i < n; i++) {
        const price = this.prices[i];
        if (now < price.end) {
          if (price.max == -1) return price;
          if (price.sold < price.max) return price;
          const _prices = this.prices.slice(i + 1);
          return (
            _prices.find(
              (_price) => _price.max === -1 || _price.sold < _price.max
            ) || this.defaultPrice
          );
        }
      }
    } catch (error) {}
    return this.defaultPrice;
  }

  get sold() {
    return this.prices.reduce((acc, cur) => (acc += cur.sold), 0);
  }

  get currentPriceDiscountPercentage() {
    try {
      if (this.defaultPrice.price == this.currentPrice.price) return 0;
      return Math.max(
        Math.round(
          ((this.defaultPrice.price - this.currentPrice.price) /
            this.defaultPrice.price) *
            100
        ),
        0
      );
    } catch (error) {}
    return 0;
  }

  get isWhitelisted() {
    return !isEmpty(this.whitelist);
  }

  checkWhitelist(identifier: string) {
    if (!this.whitelist || isEmpty(this.whitelist)) return true;
    identifier = normalizeId(identifier);
    return this.whitelist.some((w) => {
      if (w.startsWith("/") && w.endsWith("/")) {
        const regex = new RegExp(w.replace(/^\//, "").replace(/\/$/, ""), "im");
        return regex.test(identifier);
      }
      return w === identifier;
    });
  }
}
